import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import logger from '@/utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

// Use sessionStorage instead of localStorage — token clears when browser tab closes
const TOKEN_KEY = 'admin_token';

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAdmin(null);
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/admin/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAdmin(response.data);
        } catch (error) {
          logger.error('Token verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/admin/login`, {
      email,
      password
    });
    const { access_token, admin: adminData } = response.data;
    sessionStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setAdmin(adminData);
    return adminData;
  };

  const isSuperAdmin = useCallback(() => {
    return admin?.role === 'super_admin';
  }, [admin]);

  const hasPermission = useCallback((permission) => {
    if (!admin) return false;
    if (admin.role === 'super_admin') return true;
    return (admin.permissions || []).includes(permission);
  }, [admin]);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  return (
    <AdminContext.Provider value={{
      admin,
      token,
      loading,
      login,
      logout,
      isSuperAdmin,
      hasPermission,
      getAuthHeaders
    }}>
      {children}
    </AdminContext.Provider>
  );
};
